// app/composables/useApiConfig.ts - API 配置与公共字段

import type { ApiConfig, Common } from '~/types/api'

// 生成 UUID（仅在浏览器不支持 crypto.randomUUID 时作为备用）
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function readCookie(name: string): string | null {
  if (import.meta.server) return null
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

function writeCookie(name: string, value: string, days = 365) {
  if (import.meta.server) return
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; Expires=${expires}; Path=/; SameSite=Lax`
}

function getDeviceId(): string {
  // TODO(Enhance): SSR 首次请求由服务端下发 Set-Cookie: cds_device_id=<uuid>
  if (import.meta.server) return ''
  const LS_KEY = 'cds.device_id'
  const CK_KEY = 'cds_device_id'

  // 优先从 Cookie 读，确保与 SSR 一致
  let id = readCookie(CK_KEY) || localStorage.getItem(LS_KEY)

  if (!id) {
    // 使用更随机的 API，回退到简易 UUID
    const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : generateUUID()
    id = `web_${uuid}`
    // 双写：Cookie + localStorage 提升持久性
    writeCookie(CK_KEY, id, 365)
    localStorage.setItem(LS_KEY, id)
  } else {
    // 若仅存在于 localStorage，补写到 Cookie，便于后续 SSR 使用
    if (!readCookie(CK_KEY)) writeCookie(CK_KEY, id, 365)
    if (!localStorage.getItem(LS_KEY)) localStorage.setItem(LS_KEY, id)
  }

  return id
}

export function useApiConfig() {
  const runtime = useRuntimeConfig()

  // 获取当前语言：客户端和 SSR 都从 URL/Cookie 读取（不依赖 useI18n）
  function getLang(): string {
    if (import.meta.client) {
      // 客户端：从 URL 路径或 cookie 获取（不使用 useI18n，避免 composables 上下文问题）
      try {
        // 1. 优先从 URL 路径获取（与 SSR 保持一致）
        const path = window.location.pathname
        const match = path.match(/^\/([a-z]{2})(\/|$)/)
        if (match && match[1]) {
          const code = match[1]
          const supportedLangs: string[] = ['en', 'ja', 'ko', 'fr']
          if (supportedLangs.includes(code)) {
            return code
          }
        }
        
        // 2. 回退到 cookie（i18n 会写入 cookie）
        const cookieMatch = document.cookie.match(/(?:^|;\s*)i18n_redirected=([^;]+)/)
        if (cookieMatch && cookieMatch[1]) {
          const cookieLang = decodeURIComponent(cookieMatch[1])
          // cookie 值可能是 'zh' 或 'en' 等
          const supportedLangs: string[] = ['zh', 'en', 'ja', 'ko', 'fr']
          if (supportedLangs.includes(cookieLang)) {
            return cookieLang
          }
        }
        
        // 3. 默认中文
        return 'zh'
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('[useApiConfig] 客户端获取语言失败，使用默认值 zh', error)
        return 'zh'
      }
    } else {
      // SSR：从路由路径动态推断，支持所有配置的语言
      let currentPath = 'unknown'
      try {
        const { locales } = useI18n()
        const route = useRoute()
        currentPath = route.path
        
        // 从 i18n 配置获取所有支持的语言代码
        const supportedLocales = (locales.value as any[]).map(l => l.code as string)
        
        // 遍历匹配路由路径（如 /en/xxx -> 'en', /ja/xxx -> 'ja'）
        for (const code of supportedLocales) {
          if (currentPath === `/${code}` || currentPath.startsWith(`/${code}/`)) {
            return code
          }
        }
        
        // 未匹配到路径前缀，返回默认语言 'zh'
        // (根据 nuxt.config.ts 的 defaultLocale: 'zh' 和 strategy: 'prefix_except_default')
        return 'zh'
      } catch (error) {
        // 兜底：无法获取 i18n 配置时
        // eslint-disable-next-line no-console
        console.warn('[useApiConfig] SSR 获取语言失败，使用默认值 zh', {
          error,
          path: currentPath
        })
        return 'zh'
      }
    }
  }

  // 使用无 this 绑定的函数，避免解构调用时丢失上下文
  const getConfig = (): ApiConfig => ({
    baseURL: runtime.public.apiBaseURL,
    timeout: Number(runtime.public.apiTimeout || 30000),
    appId: runtime.public.appId,
    accessKey: runtime.public.accessKey,
    accessSecret: runtime.public.accessSecret,
    channel: runtime.public.channel,
    version: runtime.public.version,
    platform: 'web',
    lang: getLang()
  })

  const getCommonRequestFields = (): Partial<Common> => {
    const cfg = getConfig()
    return {
      app_id: cfg.appId,
      channel: cfg.channel,
      version: cfg.version,
      platform: cfg.platform,
      lang: cfg.lang,
      device_id: getDeviceId()
      // 注意：移动端协议中请求不包含 request_id，这里不下发，仅本地日志可用
    }
  }

  return { getConfig, getCommonRequestFields }
}
